import { useState } from "react";
import toast from "react-hot-toast";
import { Dialog, Button, Badge, cn } from "@ui";
import { Eye } from "lucide-react";
import { useReveal, useStatusFor } from "./contact-status-context";

interface RevealDialogProps {
  open: boolean;
  onClose: () => void;
  // One id for the badge dropdown, many for the batch selection bar.
  userIds: number[];
}

function messageForReason(reason?: string): string {
  switch (reason) {
    case "subscription_required":
      return "Un abonnement est requis pour afficher les coordonnées.";
    case "quota_blocked":
      return "Votre quota d'affichage de coordonnées est atteint.";
    case "privacy_locked":
      return "Ce contact est privé.";
    default:
      return "Impossible d'afficher les coordonnées pour le moment.";
  }
}

// React replica of the server bulk-reveal modal, for 1..N profiles. Gates on
// the same policy the server enforces, POSTs the canonical reveal per profile,
// then refreshes the badges in place. Stacks above the table expand dialog.
//
// Robust to missing/not-yet-loaded statuses: counts the *selection*, treats an
// unknown status as still-to-reveal (the server is the final gate), and only
// reports "déjà affichées" when every selected profile is *known* revealed.
export function RevealDialog({ open, onClose, userIds }: RevealDialogProps) {
  const reveal = useReveal();
  const statusFor = useStatusFor();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedCount = userIds.length;
  const statuses = userIds
    .map((id) => statusFor(id))
    .filter((s): s is NonNullable<typeof s> => Boolean(s));

  // Ids still to reveal: unknown statuses are attempted (server decides), known
  // ones only if not already revealed and not owner-locked.
  const revealableIds = userIds.filter((id) => {
    const s = statusFor(id);
    return !s || (!s.revealed && !s.privacy_locked);
  });
  const count = revealableIds.length;
  const lockedCount = statuses.filter((s) => s.privacy_locked).length;

  // Subscription / quota are viewer-wide; fall back to "allow, server gates"
  // when we have no status data yet.
  const subscribed = statuses.length === 0 || statuses.every((s) => s.subscribed);
  const quotaRemaining = statuses[0]?.reveal_quota_remaining ?? null;
  const quotaShort = quotaRemaining != null && quotaRemaining < count;

  const nothingToReveal = count === 0 && subscribed && !quotaShort;
  const blocked = !reveal || count === 0 || !subscribed || quotaShort;

  const note: { tone: "error" | "info"; text: string } | null = !subscribed
    ? { tone: "error", text: "Un abonnement est requis pour afficher les coordonnées." }
    : quotaShort
      ? {
          tone: "error",
          text: `Quota insuffisant : ${quotaRemaining} affichage(s) restant(s) pour ${count} contact(s).`,
        }
      : lockedCount > 0 && count === 0
        ? {
            tone: "info",
            text: "Ces contacts sont privés — contactez leurs propriétaires pour obtenir leurs infos.",
          }
        : count === 0
          ? {
              tone: "info",
              text:
                selectedCount > 1
                  ? "Les coordonnées de tous les contacts sélectionnés sont déjà affichées."
                  : "Les coordonnées de ce contact sont déjà affichées.",
            }
          : null;

  const title =
    selectedCount > 1
      ? `Afficher les coordonnées de ${selectedCount} contacts`
      : "Afficher les coordonnées";

  const handleConfirm = async () => {
    if (!reveal || blocked) return;
    setError(null);
    setLoading(true);
    const results = await Promise.all(revealableIds.map((id) => reveal(id)));
    setLoading(false);
    const failed = results.find((r) => !r.revealed);
    if (failed) {
      setError(messageForReason(failed.reason));
      return;
    }
    toast.success(
      count > 1
        ? `Coordonnées affichées pour ${count} contacts.`
        : "Coordonnées affichées.",
    );
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} title={title} className="max-w-lg">
      <div className="space-y-5">
        <p className="text-gray-600">
          Voulez-vous afficher les coordonnées de{" "}
          {selectedCount > 1 ? "ces contacts" : "ce contact"} ?
        </p>

        {quotaRemaining != null && (
          <p className="text-sm text-gray-500">
            Quota restant : {quotaRemaining} affichage(s).
          </p>
        )}

        {statuses.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {statuses.map((s) => (
              <Badge key={s.user_id} label={s.display_name} color="default" />
            ))}
          </div>
        )}

        {(error || note) && (
          <div
            className={cn(
              "rounded-lg px-3 py-2 text-sm ring-1",
              error || note?.tone === "error"
                ? "bg-red-50 text-red-700 ring-red-200"
                : "bg-emerald-50 text-emerald-700 ring-emerald-200",
            )}
          >
            {error ?? note?.text}
          </div>
        )}

        <div className="flex justify-end gap-3 pt-2">
          {nothingToReveal ? (
            <Button onClick={onClose}>Fermer</Button>
          ) : (
            <>
              <Button variant="white" onClick={onClose} disabled={loading}>
                Annuler
              </Button>
              <Button
                icon={Eye}
                onClick={handleConfirm}
                disabled={blocked}
                loading={loading}
              >
                Confirmer
              </Button>
            </>
          )}
        </div>
      </div>
    </Dialog>
  );
}
